(function () {
  'use strict';

  const sectionHandlers = new WeakMap();

  // Scroll events carry no "ended" signal — treat this much silence as settled.
  const SCROLL_SETTLE_MS = 150;

  const initHeroBannerCarousel = (section) => {
    if (!section || section.dataset.initialized === 'true') return;

    const track = section.querySelector('[data-hero-banner-track]');
    const slides = section.querySelectorAll('[data-hero-banner-slide]');
    const dots = section.querySelectorAll('[data-hero-banner-dot]');
    const videoWrappers = section.querySelectorAll('[data-hero-banner-video]');
    const totalSlides = slides.length;
    const isCarousel = !!track && totalSlides > 1;

    const muteAllVideos = () => {
      videoWrappers.forEach((wrapper) => {
        const video = wrapper.querySelector('video');
        const toggle = wrapper.querySelector('[data-hero-banner-sound-toggle]');
        if (!video || !toggle) return;
        video.muted = true;
        wrapper.dataset.userUnmuted = 'false';
        toggle.dataset.muted = 'true';
        toggle.setAttribute('aria-label', 'Unmute video');
      });
    };

    // Sound is scoped to whichever slide is on screen — the toggle sits inside
    // the slide's link, so clicks must be stopped from also following it.
    videoWrappers.forEach((wrapper) => {
      const video = wrapper.querySelector('video');
      const toggle = wrapper.querySelector('[data-hero-banner-sound-toggle]');
      if (!video || !toggle) return;

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        video.muted = !video.muted;
        if (!video.muted) video.play().catch(() => {});
        wrapper.dataset.userUnmuted = video.muted ? 'false' : 'true';
        toggle.dataset.muted = video.muted ? 'true' : 'false';
        toggle.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      });
    });

    let videoVisibilityObserver = null;
    if (videoWrappers.length && 'IntersectionObserver' in window) {
      // Force-mute once the banner leaves view, but remember a manual unmute so
      // sound can resume when it scrolls back in — never resume unprompted.
      videoVisibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            videoWrappers.forEach((wrapper) => {
              const video = wrapper.querySelector('video');
              const toggle = wrapper.querySelector('[data-hero-banner-sound-toggle]');
              if (!video || !toggle) return;

              if (entry.isIntersecting) {
                if (wrapper.dataset.userUnmuted === 'true' && video.muted) {
                  video.muted = false;
                  toggle.dataset.muted = 'false';
                  toggle.setAttribute('aria-label', 'Mute video');
                  video.play().catch(() => {});
                }
              } else if (!video.muted) {
                wrapper.dataset.userUnmuted = 'true';
                video.muted = true;
                toggle.dataset.muted = 'true';
                toggle.setAttribute('aria-label', 'Unmute video');
              }
            });
          });
        },
        { threshold: 0.25 }
      );

      videoVisibilityObserver.observe(section);
    }

    if (!isCarousel) {
      if (!videoWrappers.length) return;
      sectionHandlers.set(section, { videoVisibilityObserver });
      section.dataset.initialized = 'true';
      return;
    }

    const speed = parseInt(section.dataset.autoplaySpeed, 10) || 4000;
    const autoplayEnabled = section.dataset.autoplayEnabled !== 'false';
    let currentIndex = 0;
    let autoplayTimer = null;
    let isOffScreen = false;
    let isHovering = false;
    let isUserScrolling = false;
    let isProgrammaticScroll = false;
    let scrollFrame = null;
    let settleTimer = null;

    // The track is a scroll-snap container, so slide width is whatever the
    // browser laid out — never a vw guess.
    const slideWidth = () => slides[0].clientWidth;

    const updateDots = () => {
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === currentIndex));
    };

    const scrollToIndex = (index, behavior = 'smooth') => {
      isProgrammaticScroll = true;
      currentIndex = ((index % totalSlides) + totalSlides) % totalSlides;
      track.scrollTo({ left: currentIndex * slideWidth(), behavior });
      updateDots();
      muteAllVideos();
    };

    const stopAutoplay = () => {
      clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    // Every reason to stay paused is checked here, so no single event can
    // resume autoplay while another still wants it stopped.
    const startAutoplay = () => {
      if (!autoplayEnabled || isHovering || isUserScrolling || isOffScreen || document.hidden) return;
      stopAutoplay();
      autoplayTimer = setInterval(() => scrollToIndex(currentIndex + 1), speed);
    };

    const onScrollSettled = () => {
      isProgrammaticScroll = false;
      if (!isUserScrolling) return;
      isUserScrolling = false;
      startAutoplay();
    };

    // Native scrolling drives the dots — trackpad, touch, and scrollTo alike.
    const onScroll = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onScrollSettled, SCROLL_SETTLE_MS);

      // A scroll we didn't start is the user swiping. Hold autoplay back so it
      // can't yank the track mid-gesture — the only pause that works on touch.
      if (!isProgrammaticScroll && !isUserScrolling) {
        isUserScrolling = true;
        stopAutoplay();
      }

      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = null;
        const index = Math.round(track.scrollLeft / slideWidth());
        if (index !== currentIndex && index >= 0 && index < totalSlides) {
          currentIndex = index;
          updateDots();
          muteAllVideos();
        }
      });
    };

    track.addEventListener('scroll', onScroll, { passive: true });

    section.addEventListener('mouseenter', () => {
      isHovering = true;
      stopAutoplay();
    });

    section.addEventListener('mouseleave', () => {
      isHovering = false;
      startAutoplay();
    });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        scrollToIndex(i);
        stopAutoplay();
        startAutoplay();
      });
    });

    const onResize = () => scrollToIndex(currentIndex, 'auto');

    window.addEventListener('resize', onResize);

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        isOffScreen = !entry.isIntersecting;
        if (entry.isIntersecting) {
          startAutoplay();
        } else {
          stopAutoplay();
        }
      });
    }, { threshold: 0 });

    observer.observe(section);

    const clearTimers = () => {
      clearTimeout(settleTimer);
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
    };

    sectionHandlers.set(section, {
      onResize,
      onVisibilityChange,
      stopAutoplay,
      clearTimers,
      observer,
      videoVisibilityObserver,
    });

    updateDots();
    startAutoplay();
    section.dataset.initialized = 'true';
  };

  const cleanupSection = (section) => {
    const handlers = sectionHandlers.get(section);
    if (!handlers) return;
    handlers.stopAutoplay?.();
    handlers.clearTimers?.();
    if (handlers.onResize) window.removeEventListener('resize', handlers.onResize);
    if (handlers.onVisibilityChange) document.removeEventListener('visibilitychange', handlers.onVisibilityChange);
    handlers.observer?.disconnect();
    handlers.videoVisibilityObserver?.disconnect();
    sectionHandlers.delete(section);
  };

  const initAll = () => {
    document.querySelectorAll('[data-hero-banner-carousel]:not([data-initialized="true"])').forEach(initHeroBannerCarousel);
  };

  document.addEventListener('DOMContentLoaded', initAll);

  document.addEventListener('shopify:section:load', (e) => {
    const carousel = e.target.querySelector('[data-hero-banner-carousel]');
    if (carousel) initHeroBannerCarousel(carousel);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const carousel = e.target.querySelector('[data-hero-banner-carousel]');
    if (carousel) cleanupSection(carousel);
  });
})();
